import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '../../components/ui';
import { CONVITE_PENDENTE_KEY, useAuth } from '../../contexts/AuthContext';
import { EMPRESA_ATIVA_KEY, valorSalvoDaEmpresa } from '../../contexts/empresaAtiva';
import { usePreferences } from '../../contexts/PreferencesContext';
import { api } from '../../services';
import type { ConvitePublico } from '../../types';
import logoClaro from '../../assets/Logo_claro.png';
import logoEscuro from '../../assets/Logo_escuro.png';
import styles from '../ResetPassword/ResetPassword.module.css';
import { safeLocalStorage, safeSessionStorage } from '../../utils/safeStorage';

// Mesma regra de senha do cadastro (backend: RegisterDto / CadastroPorConviteDto).
const SENHA_FORTE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

/**
 * Aceite de convite (plano de convites, Fase 3). Abre logado ou não:
 * - sem conta com o e-mail convidado: cria a conta aqui e já entra;
 * - com conta e deslogado: entra com ela e volta para cá para aceitar;
 * - logado com o e-mail convidado: aceita com um clique;
 * - logado com outro e-mail: precisa sair e entrar com a conta certa.
 */
export function ConviteAceite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const navigate = useNavigate();
    const { user, logout, entrarComSessao, carregarFiliais } = useAuth();
    const { prefs } = usePreferences();

    const [convite, setConvite] = useState<ConvitePublico | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [nome, setNome] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!token) {
            setErro('Link de convite inválido.');
            setCarregando(false);
            return;
        }
        api.consultarConvite(token)
            .then(setConvite)
            .catch(() => setErro('Este convite não existe mais. Peça um novo para quem convidou você.'))
            .finally(() => setCarregando(false));
    }, [token]);

    const entrarNaEmpresa = async (empresaId?: string) => {
        safeSessionStorage.removeItem(CONVITE_PENDENTE_KEY);
        // Quem ja tinha empresa abre na que acabou de aceitar, nao na propria.
        if (empresaId) {
            safeLocalStorage.setItem(EMPRESA_ATIVA_KEY, valorSalvoDaEmpresa(empresaId));
            safeLocalStorage.removeItem('filialAtiva');
        }
        await carregarFiliais();
        navigate('/home');
    };

    const aceitar = async () => {
        setEnviando(true);
        setErro('');
        try {
            const { empresaId } = await api.aceitarConvite(token);
            await entrarNaEmpresa(empresaId);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível aceitar o convite');
        } finally {
            setEnviando(false);
        }
    };

    const cadastrar = async (e: FormEvent) => {
        e.preventDefault();
        setErro('');
        if (nome.trim().length < 2) return setErro('Informe seu nome.');
        if (!SENHA_FORTE.test(senha)) {
            return setErro('A senha deve ter no mínimo 8 caracteres, com letra maiúscula, minúscula, número e caractere especial.');
        }
        if (senha !== confirmacao) return setErro('As senhas não conferem.');

        setEnviando(true);
        try {
            await entrarComSessao(await api.cadastrarPorConvite({ token, name: nome.trim(), password: senha }));
            await entrarNaEmpresa();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar a conta');
        } finally {
            setEnviando(false);
        }
    };

    const irParaLogin = () => {
        safeSessionStorage.setItem(CONVITE_PENDENTE_KEY, token);
        navigate('/login');
    };

    const renderCorpo = () => {
        if (carregando) return <p className={styles.subtitle}>Carregando convite...</p>;
        if (!convite) return <div className={styles.error}>{erro}</div>;
        if (convite.aceito) {
            return (
                <>
                    <div className={styles.successMessage}>Este convite já foi aceito.</div>
                    <button type="button" className={styles.backLink} onClick={() => navigate(user ? '/home' : '/login')}>
                        {user ? 'Ir para o sistema' : 'Entrar'}
                    </button>
                </>
            );
        }
        if (convite.expirado) {
            return <div className={styles.error}>Este convite expirou. Peça um novo para quem convidou você.</div>;
        }

        const mesmaConta = user && user.email.toLowerCase() === convite.email.toLowerCase();

        if (user && !mesmaConta) {
            return (
                <>
                    <div className={styles.error}>
                        Você está conectado como {user.email}, mas o convite é para {convite.email}.
                    </div>
                    <Button type="button" fullWidth onClick={() => logout()}>Sair e entrar com a outra conta</Button>
                </>
            );
        }

        if (mesmaConta) {
            return (
                <>
                    {erro && <div className={styles.error}>{erro}</div>}
                    <Button type="button" fullWidth isLoading={enviando} onClick={aceitar}>
                        Aceitar e acessar {convite.empresa}
                    </Button>
                </>
            );
        }

        if (convite.contaExiste) {
            return (
                <>
                    <p className={styles.subtitle}>Você já tem conta com {convite.email}. Entre com ela para aceitar.</p>
                    <Button type="button" fullWidth onClick={irParaLogin}>Entrar para aceitar</Button>
                </>
            );
        }

        return (
            <form className={styles.form} onSubmit={cadastrar}>
                <p className={styles.subtitle}>Crie sua senha para acessar com {convite.email}.</p>
                {erro && <div className={styles.error}>{erro}</div>}
                <Input label="Seu nome" value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" fullWidth />
                <Input
                    type="password"
                    label="Senha"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    fullWidth
                />
                <Input
                    type="password"
                    label="Confirmar senha"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    value={confirmacao}
                    onChange={(e) => setConfirmacao(e.target.value)}
                    fullWidth
                />
                <Button type="submit" fullWidth isLoading={enviando}>Criar conta e aceitar</Button>
            </form>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.logo}>
                    <img src={prefs.darkMode ? logoClaro : logoEscuro} alt="Financeiro Diário" className={styles.logoImg} />
                </div>
                <div className={styles.header}>
                    <h1 className={styles.title}>{convite ? `Convite para ${convite.empresa}` : 'Convite'}</h1>
                    {convite?.convidadoPor && (
                        <p className={styles.subtitle}>{convite.convidadoPor} convidou você para acessar esta empresa.</p>
                    )}
                </div>
                {renderCorpo()}
            </div>
        </div>
    );
}
